import { z } from "zod";

/**
 * Narrow, hand-written slice of Google Identity Services (GSI) — only what
 * the `/signup` One Tap prompt uses. Google ships no types we depend on, and
 * the `google` global is shared with the Google Maps API (`google.maps`), so
 * the global is read through a runtime guard instead of being declared.
 */

export const GOOGLE_IDENTITY_SERVICES_SCRIPT_URL =
  "https://accounts.google.com/gsi/client";

export interface GoogleCredentialResponse {
  /** The ID token (a JWT) of the account the visitor picked. */
  credential?: string | undefined;
}

export interface GoogleOneTapConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
}

export interface GoogleAccountsId {
  initialize: (config: GoogleOneTapConfig) => void;
  prompt: () => void;
  cancel: () => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isGoogleAccountsId(value: unknown): value is GoogleAccountsId {
  return (
    isRecord(value) &&
    typeof value["initialize"] === "function" &&
    typeof value["prompt"] === "function" &&
    typeof value["cancel"] === "function"
  );
}

/** `google.accounts.id`, or `undefined` while GSI is not loaded (the Maps API alone does not provide it). */
export function getGoogleAccountsId(): GoogleAccountsId | undefined {
  const google: unknown = Reflect.get(window, "google");
  if (!isRecord(google) || !isRecord(google["accounts"])) {
    return undefined;
  }
  const id = google["accounts"]["id"];
  return isGoogleAccountsId(id) ? id : undefined;
}

function loadError(reason: string): Error {
  return new Error(`Google Identity Services unavailable: ${reason}`);
}

/**
 * Loads the GSI client script once and resolves with `google.accounts.id`.
 * A concurrent call shares the script already in the page. A failed script
 * is removed again, so a later call (e.g. revisiting `/signup`) can retry.
 */
export function loadGoogleIdentityServices(): Promise<GoogleAccountsId> {
  return new Promise((resolve, reject) => {
    const ready = getGoogleAccountsId();
    if (ready) {
      resolve(ready);
      return;
    }

    let script = document.head.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_IDENTITY_SERVICES_SCRIPT_URL}"]`
    );
    if (!script) {
      script = document.createElement("script");
      script.src = GOOGLE_IDENTITY_SERVICES_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const loading = script;

    const stopListening = () => {
      loading.removeEventListener("load", onLoad);
      loading.removeEventListener("error", onError);
    };
    const onLoad = () => {
      stopListening();
      const id = getGoogleAccountsId();
      if (id) {
        resolve(id);
      } else {
        loading.remove();
        reject(loadError("the script did not provide google.accounts.id"));
      }
    };
    const onError = () => {
      stopListening();
      loading.remove();
      reject(loadError("the script failed to load"));
    };
    loading.addEventListener("load", onLoad);
    loading.addEventListener("error", onError);
  });
}

const credentialPayloadSchema = z.object({ email: z.string().email() });

function decodeBase64UrlToUtf8(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  return new TextDecoder().decode(
    Uint8Array.from(binary, character => character.charCodeAt(0))
  );
}

/**
 * Reads the `email` claim from the payload of a Google credential JWT
 * **without verifying the signature**. The result is only ever a login hint
 * for Google's account chooser — never proof of identity — so a forged
 * credential gains nothing. Returns `undefined` for anything unreadable.
 */
export function decodeCredentialEmail(credential: string): string | undefined {
  const payload = credential.split(".")[1];
  if (!payload) {
    return undefined;
  }
  try {
    const claims = credentialPayloadSchema.safeParse(
      JSON.parse(decodeBase64UrlToUtf8(payload))
    );
    return claims.success ? claims.data.email : undefined;
  } catch {
    return undefined;
  }
}
